import { useEffect, useRef, useState } from 'react';
import { Popover } from '~/commonComponents/Popover/Popover';
import StarIcon from '~/images/icons/StarIcon';
import { RatingContent } from './RatingContent';
import { Box, Row, Text } from '~/commonComponents';
import { Colors } from '~/themes/colors';
import { IProfessionalCardRating } from '../../interfaces';

interface IProfessionalRatingProps {
  rating?: IProfessionalCardRating;
  isAvailable?: boolean;
}

export const ProfessionalRating = ({ rating, isAvailable = true }: IProfessionalRatingProps) => {
  const targetRef = useRef<HTMLSpanElement | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<'top' | 'bottom'>('bottom');

  const showRatingBreakdown =
    rating?.professionalSkills != null ||
    rating?.interpersonalSkills != null ||
    rating?.timeManagement != null;

  const updatePopoverPosition = () => {
    if (!targetRef.current) return;

    const rect = targetRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const buffer = 250; // adjust as needed for popover height

    if (viewportHeight - rect.bottom < buffer) {
      setPopoverPosition('top');
    } else {
      setPopoverPosition('bottom');
    }
  };

  useEffect(() => {
    updatePopoverPosition();

    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition);
    };
  }, []);

  if (rating == null) {
    return null;
  }

  return (
    <Row alignItems="center" gap={2}>
      <Box alignItems="center" opacity={isAvailable ? 1 : 0.5}>
        <StarIcon width={20} height={20} />
      </Box>

      {showRatingBreakdown ? (
        <Popover
          position={popoverPosition}
          trigger="hover"
          Target={() => (
            <span
              ref={targetRef}
              style={{ textDecoration: 'underline', color: Colors.neutral_600 }}
            >
              <Text bold fontSize={20} opacity={isAvailable ? 1 : 0.5}>
                {rating?.averageRating?.toFixed(1)}
              </Text>
            </span>
          )}
          Content={() => (
            <RatingContent
              professionalSkills={rating.professionalSkills ?? 5}
              interpersonalSkills={rating.interpersonalSkills ?? 5}
              timeManagement={rating.timeManagement ?? 5}
            />
          )}
        />
      ) : (
        <span style={{ textDecoration: 'underline', color: Colors.neutral_600 }}>
          <Text bold fontSize={20}>
            {rating?.averageRating?.toFixed(1)}
          </Text>
        </span>
      )}
    </Row>
  );
};
